import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useCallback, useMemo } from 'react';

import { DynamicImage, enLoading } from 'components/Image';
import { SportsCompetitor } from 'types/swagger';
import classNames from 'classnames';
import { format } from 'date-fns';
import { isNotEmpty } from 'utility/functions';
import { selectSelectedAvvenimento } from 'features/sport/selectors';
import { setAvvenimentoSelected } from 'features/sport/marcatoriSlice';
import styles from './SportMarcatoriCarouselCard.module.scss';
import { useAvvenimento } from 'features/sport/hooks';
import { useTranslation } from 'hooks/useLingUI';

export type SportMarcatoriCarouselCardProps = {
  avvenimentoKey: string;
  competitorEnabled?: 1 | 2 | 'both';
};

type CompetitorButtonProps = {
  competitor: SportsCompetitor;
  isSelected: boolean;
  handleClick: Function;
  isDisabled?: boolean;
};

export const SportMarcatoriCarouselCard = ({
  avvenimentoKey,
  competitorEnabled = 'both',
}: SportMarcatoriCarouselCardProps) => {
  const dispatch = useAppDispatch();
  const selectedAvvenimento = useTypedSelector(selectSelectedAvvenimento);
  const { dataOra, firstCompetitor, secondCompetitor } = useAvvenimento(avvenimentoKey);

  const date = format(new Date(dataOra), 'dd/MM');
  const time = format(new Date(dataOra), 'HH:mm');
  const competitors: Array<SportsCompetitor> = [firstCompetitor ?? {}, secondCompetitor ?? {}];

  const isAvvenimentoSelected = useMemo(
    () => selectedAvvenimento?.avvenimentoKey === avvenimentoKey,
    [selectedAvvenimento, avvenimentoKey]
  );

  const competitor = useMemo(
    () => (isAvvenimentoSelected ? selectedAvvenimento?.competitor : null),
    [isAvvenimentoSelected, selectedAvvenimento]
  );

  const handleClick = useCallback(
    (competitorNumber: 1 | 2) => {
      if (competitorEnabled === 'both' || competitorEnabled === competitorNumber) {
        dispatch(setAvvenimentoSelected({ avvenimentoKey, competitor: competitorNumber }));
      }
    },
    [avvenimentoKey, competitorEnabled, dispatch]
  );

  return (
    <article className={styles.container}>
      <span className={styles.title}>
        {date} - {time}
      </span>
      <div className={styles.btnContainer}>
        <CompetitorButton
          competitor={competitors[0]}
          handleClick={() => handleClick(1)}
          isSelected={isAvvenimentoSelected && competitor === 1}
          isDisabled={competitorEnabled !== 1 && competitorEnabled !== 'both'}
        />
        <CompetitorButton
          competitor={competitors[1]}
          handleClick={() => handleClick(2)}
          isSelected={isAvvenimentoSelected && competitor === 2}
          isDisabled={competitorEnabled !== 2 && competitorEnabled !== 'both'}
        />
      </div>
    </article>
  );
};

const CompetitorButton = ({ competitor, handleClick, isSelected, isDisabled }: CompetitorButtonProps) => {
  const { t } = useTranslation();
  const label = t(competitor.descrizioneTrKey, competitor.descrizione);

  return (
    <button
      type="button"
      onClick={() => handleClick()}
      className={classNames(styles.button, {
        [styles.active]: isSelected,
        [styles.disabled]: isDisabled,
      })}
      data-selected={isSelected}
    >
      {isNotEmpty(competitor.iconSrc) && (
        <span className={styles.image}>
          <DynamicImage
            alt={competitor.descrizione ?? 'competitor_img'}
            src={`${competitor.iconSrc}`}
            fill
            loading={enLoading.lazy}
            decoding="async"
          />
        </span>
      )}

      <span className={styles.competitor}>{label}</span>
    </button>
  );
};
